import { ComponentBase } from "../core/ComponentBase.js";

export class DocumentWorkspaceManager extends ComponentBase {
    static styles = `
        .document-workspace-manager {
            height: 100%;
            min-height: 0;
            overflow: hidden;
            width: 100%;
        }
        .document-workspace-error {
            margin: 1rem;
        }
    `;

    constructor(config) {
        super(config);
        this.delegate = null;
        this.delegateType = null;
        this.syncProjectContext = this.config.syncProjectContext !== false;
        this._boundHandleProjectChange = this.handleProjectChange.bind(this);
        this.applyStyles();
    }

    async initialize() {
        if (this.syncProjectContext) {
            this.appClient.events.local.on('projectSelected', this._boundHandleProjectChange);
        }
        const selectedProject = this.syncProjectContext && !this.config.contextId
            ? this.appClient.getSharedData('selectedProject')
            : null;
        if (selectedProject) {
            const contextId = selectedProject._id;
            const userId = this.appClient.getSharedData('userId');
            this.config = {
                ...this.config,
                contextId,
                contextType: contextId ? 'project' : 'user',
                ownerId: userId
            };
        }
        await super.initialize();
    }

    async handleProjectChange({ projectData }) {
        if (!this.syncProjectContext) return;
        const contextId = projectData ? projectData._id : null;
        const userId = this.appClient.getSharedData('userId');
        await this.updateContext({
            contextId,
            contextType: contextId ? 'project' : 'user',
            ownerId: userId
        });
        if (this.container) await this.render();
    }

    resolveDelegateType() {
        if (this.config.component) return this.config.component;
        if (this.config.provider === 'mageledgerWiki') return 'MageMapKnowledge';
        if (this.config.provider === 'kbmWorkspace') return 'document-workspace/DocumentManager';
        return 'document-workspace/DocumentManager';
    }

    resolveDelegateProps() {
        const {
            appClient,
            instanceId,
            component,
            provider,
            type,
            ...props
        } = this.config;

        return {
            provider,
            ...props,
            ...(this.config.props || {})
        };
    }

    async updateContext(context = {}) {
        this.config = { ...this.config, ...context };
        if (typeof this.delegate?.updateContext === 'function') {
            await this.delegate.updateContext(context);
        } else if (this.delegate?.docSelector) {
            await this.delegate.docSelector.updateContext(context);
        }
    }

    async render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'document-workspace-manager';
        }

        this.container.innerHTML = '';
        const delegateType = this.resolveDelegateType();

        try {
            if (!this.delegate || this.delegateType !== delegateType) {
                if (this.delegate?.destroy) await this.delegate.destroy();
                this.delegateType = delegateType;
                this.delegate = await this.createManagedComponent(delegateType, null, {
                    instanceId: `${this.config.instanceId || 'document-workspace'}-${delegateType}`,
                    ...this.resolveDelegateProps()
                });
            }
            this.container.appendChild(await this.delegate.render());
        } catch (error) {
            this.log.error(`Failed to render document workspace delegate ${delegateType}:`, error);
            const safeDelegateType = String(delegateType).replace(/[&<>"']/g, char => ({
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#39;'
            }[char]));
            this.container.innerHTML = `
                <div class="alert alert-danger document-workspace-error">
                    Failed to load document workspace component <code>${safeDelegateType}</code>.
                </div>
            `;
        }

        return this.container;
    }

    async destroy() {
        if (this.syncProjectContext) {
            this.appClient.events.local.off('projectSelected', this._boundHandleProjectChange);
        }
        if (this.delegate?.destroy) await this.delegate.destroy();
        await super.destroy();
    }
}
